import {observable, action, computed} from 'mobx';
import uris from '~/uris';
import axiosInstance from '~/utils/axiosInstance';
import SessionStore from '~/stores/SessionStore';
import {WORKING_APP_ID_STORAGE_KEY, VIEW_MODE_STORAGE_KEY, WORKING_MENU_CODE_STORAGE_KEY, DEFAULT_HOMEPAGE} from '~/utils/constants';
import {isMobileDevice} from '~/utils/tools';
import Categories from './models/Categories';
import App from './models/App';

class PortalStore {
  @observable queryState;

  @observable helpUrl;

  @observable notificationUrl;

  @observable isLoadingFrame = true;

  @observable isLoadingPortal = true;

  @observable isLoadingResources = true;

  @observable categoryWithApps = [];

  @observable appAssets = [];

  @observable apps = [];

  @observable workingAppId;

  @observable workingMenuCode;

  @observable currentAppMenus = [];

  @observable currentAppAssets = [];

  @observable currentAppPermissions = [];

  @observable isNewNavigation = false;

  @observable viewMode = ((mode = 0) => {
    if ([0, 1, 2].includes(parseInt(mode))) {
      return parseInt(mode);
    }
    return 0;
  })(localStorage.getItem(VIEW_MODE_STORAGE_KEY));

  @observable showMenu = true;

  @computed get workingApp() {
    let app;
    if (this.apps.length === 0) {
      return {};
    }

    if (this.workingAppId) {
      app = this.apps.find(({id}) => id === this.workingAppId);
      return app || null;
    }

    // 回到上一次退出时的页面
    if (localStorage.getItem(DEFAULT_HOMEPAGE) === 'false') {
      const idFromLocale = localStorage.getItem(WORKING_APP_ID_STORAGE_KEY);

      const appFromLocale = this.apps.find(({id}) => id === idFromLocale);

      if (appFromLocale && this.shouldRenderApp(appFromLocale)) {
        return appFromLocale;
      }
    }

    if (this.categoryWithApps.length > 0) {
      app = this.categoryWithApps[0].apps[0];
    }
    return app;
  }

  @computed get workingMenu() {
    let menu;
    if (this.currentAppMenus.length === 0) {
      return {};
    }

    const findMenuByCode = (code) => {
      let sMenu;
      if (!code) {
        return null;
      }

      this.currentAppMenus.some((item) => {
        if (item.children && item.children.length > 0) {
          sMenu = item.children.find(({code: c}) => c === code);
          if (sMenu) {
            return true;
          }
        }

        if (item.code === code) {
          sMenu = item;
          return true;
        }

        return false;
      });

      return sMenu;
    };

    if (this.workingMenuCode) {
      return findMenuByCode(this.workingMenuCode) || {};
    }

    // 回到上一次退出时的页面
    if (localStorage.getItem(DEFAULT_HOMEPAGE) === 'false') {
      const codeFromLocale = localStorage.getItem(WORKING_MENU_CODE_STORAGE_KEY);

      const menuFromLocale = findMenuByCode(codeFromLocale);

      if (menuFromLocale) {
        return menuFromLocale;
      }
    }

    const mMenu = this.currentAppMenus[0];
    if (mMenu.children && mMenu.children.length > 0) {
      menu = mMenu.children[0];
    } else {
      menu = mMenu;
    }
    return menu;
  }

  @action
  setLoadingPortal(boolean) {
    this.isLoadingPortal = boolean;
  }

  @action
  setWorkingInfo(appId, menuCode, queryState) {
    if (this.workingAppId !== appId || this.workingMenuCode !== menuCode) {
      this.isLoadingFrame = true;
    }
    if (this.workingAppId !== appId) {
      localStorage.setItem(WORKING_APP_ID_STORAGE_KEY, appId);
      this.workingAppId = appId;
    }
    if (this.workingMenuCode !== menuCode) {
      localStorage.setItem(WORKING_MENU_CODE_STORAGE_KEY, menuCode);
      this.workingMenuCode = menuCode;
    }
    if (this.queryState !== queryState) {
      this.queryState = queryState;
    }
  }

  @action
  getUserAppList() {
    this.setLoadingPortal(true);
    return axiosInstance.post(uris.getUserAppList()).then((data) => {
      const apps = [];
      const categoryWithApps = [];
      data.categories.forEach((json) => {
        json.apps.forEach((item) => {
          apps.push(item);
        });
        const avableApps = (json.apps || []).filter(app => this.shouldRenderApp(app));
        if (avableApps.length > 0) {
          categoryWithApps.push(
            this.updateCategoriesFromServer(this.categoryWithApps, json)
          );
        }
      });
      this.setCategoryWithApps(categoryWithApps);
      this.setLoadingPortal(false);
      this.setApps(apps);
      return apps;
    });
  }

  @action.bound
  updateCategoriesFromServer(arr, json) {
    let category = arr.find(({id}) => id === json.id);
    const data = json;
    data.apps = json.apps.filter(app => this.shouldRenderApp(app));
    if (!category) {
      category = new Categories(this, data);
    }
    category.updateFromJson(data);
    return category;
  }

  @action
  getAppResources(appId) {
    return axiosInstance.post(uris.getUserAppResource(), null, {
      params: {appId},
      showError: {isShow: false}
    });
  }

  @action
  getUserAppAsset(appId) {
    return axiosInstance.post(uris.getUserAppAssetTree(), null, {
      params: {appId},
      showError: {isShow: false}
    });
  }

  @action
  getCurrentAppDetails(appId) {
    this.isLoadingResources = true;
    return Promise.all([
      this.getAppResources(appId),
      this.getUserAppAsset(appId)
    ]).then((data) => {
      const [resource, assets] = data;
      if (!resource) {
        return null;
      }
      const {menus, permissions, isNewNavigation} = resource;
      this.setResources(menus, permissions, assets, isNewNavigation);
    });
  }

  @action
  setApps(data) {
    this.apps = data;
  }

  @action
  setCategoryWithApps(data) {
    this.categoryWithApps = data;
  }

  @action.bound
  setResources(menus = [], permissions = [], assets = [], isNewNavigation) {
    this.currentAppMenus = menus;
    this.currentAppPermissions = permissions;
    this.currentAppAssets = assets;
    this.isLoadingResources = false;
    this.isNewNavigation = isNewNavigation;
  }

  @action.bound
  setViewMode(mode = 0) {
    if ([0, 1, 2].includes(parseInt(mode))) {
      this.viewMode = parseInt(mode);
    }
  }

  @action.bound
  updateAppFromServer(json) {
    const app = new App(this, json.id);
    app.updateFromJson(json);
    return app;
  }

  @action.bound
  setLoadingFrame(boolean) {
    this.isLoadingFrame = boolean;
  }

  @action.bound
  setHelpUrl(url) {
    this.helpUrl = url;
  }

  @action.bound
  setNotificationUrl(url) {
    this.notificationUrl = url;
  }

  @action.bound
  toggleMenu(boolean = !this.showMenu) {
    this.showMenu = boolean;
  }

  checkMenuCodeExist(c) {
    return this.currentAppMenus.some((item) => {
      if (item.children && item.children.length > 0) {
        // eslint-disable-next-line
        const menu = item.children.find(({code}) => code === c);
        if (menu) {
          return true;
        }
      }

      if (item.code === c) {
        return true;
      }

      return false;
    });
  }

  shouldRenderApp({status, type}) {
    if (!status) {
      return false;
    }

    if (type !== 0 && type !== 1) {
      return true;
    }

    if (SessionStore.appDeviceIdentify) {
      return isMobileDevice() === type;
    }

    return true;
  }
}

export default new PortalStore();
